import type { ChartData } from "../../types/burndown.types";
import { calculateBurndownSnapshot } from "./burndown.utils";
import {
  computeRunwayMonths,
  getOrBuildCache,
  type ChartData as CacheChartData,
} from "./financial-cache.utils";
import { mapChartDataToStartup } from "./adapter.utils";

export type FundingAskPerspective = "initial_raise" | "current_position";

export interface FundingScenario {
  name: string;
  description: string;
  months: number;
  amount: number;
  urgency: "high" | "medium" | "low";
  reasoning: string;
}

export interface FundingAnalysis {
  perspective: FundingAskPerspective;
  monthlyBurn: number;
  monthlyRevenue: number;
  netBurn: number;
  currentRunwayMonths: number;
  fundingUrgency: "high" | "medium" | "low";
  optimalTiming: string;
  scenarios: FundingScenario[];
  analysisDate: Date;
  /** Cash on hand used for runway display */
  cashBalance: number;
  /** Cash subtracted from scenario asks (0 for initial raise = full ask) */
  cashUsedForScenarios: number;
  /** Initial funding configured on the startup plan */
  configuredInitialFunding: number;
}

export interface FundingAnalysisOptions {
  perspective?: FundingAskPerspective;
}

function buildScenarios(
  netBurn: number,
  cashForScenarios: number,
  runwayMonths: number,
  grossAsk: boolean,
): FundingScenario[] {
  const scenarioAmount = (months: number) =>
    grossAsk
      ? netBurn * months
      : Math.max(netBurn * months - cashForScenarios, 0);

  return [
    {
      name: "Survival Mode",
      description: grossAsk
        ? "Total capital to cover 6 months from day zero"
        : "6 months runway from your current cash position",
      months: 6,
      amount: scenarioAmount(6),
      urgency: runwayMonths < 6 ? "high" : "low",
      reasoning: grossAsk
        ? "Your opening raise to cover basic operations through first milestone"
        : "Additional capital needed to reach 6 months of runway",
    },
    {
      name: "Growth Mode",
      description: grossAsk
        ? "Total capital for ~15 months of runway from launch"
        : "15 months runway from your current cash position",
      months: 15,
      amount: scenarioAmount(15),
      urgency: runwayMonths < 12 ? "medium" : "low",
      reasoning: grossAsk
        ? "Typical seed ask — room for product development and early growth"
        : "Additional capital to fund growth from where you are today",
    },
    {
      name: "Series Prep",
      description: grossAsk
        ? "Total capital for ~24 months from launch"
        : "24 months runway from your current cash position",
      months: 24,
      amount: scenarioAmount(24),
      urgency: runwayMonths < 18 ? "medium" : "low",
      reasoning: grossAsk
        ? "Strong initial raise — time to hit metrics before the next round"
        : "Bridge or extension to reach metrics for the next round",
    },
  ];
}

/**
 * Funding ASK analysis aligned with timeline burndown math (includes customer projections).
 */
export function calculateFundingAnalysis(
  data: ChartData,
  analysisDate: Date,
  cashBalance?: number,
  options: FundingAnalysisOptions = {},
): FundingAnalysis {
  const perspective = options.perspective ?? "current_position";
  const configuredInitialFunding = data.initialBalance ?? 0;
  const isInitialRaise = perspective === "initial_raise";

  const effectiveDate = isInitialRaise ? data.startDate : analysisDate;
  const snapshot = calculateBurndownSnapshot(data, effectiveDate);

  const cash = isInitialRaise
    ? 0
    : (cashBalance ?? snapshot.cashBalance);
  const cashUsedForScenarios = isInitialRaise ? 0 : cash;

  const monthlyBurn = snapshot.monthlyBurn;
  const monthlyRevenue = snapshot.monthlyRevenue;
  const netBurn = Math.max(0, monthlyBurn - monthlyRevenue);

  const cache = getOrBuildCache(
    mapChartDataToStartup(data) as unknown as CacheChartData,
  );
  const runwayRaw = computeRunwayMonths(
    cache,
    effectiveDate,
    cash,
    snapshot.netMonthlyChange,
  );
  const currentRunwayMonths =
    runwayRaw === Infinity ? Infinity : Math.round(runwayRaw * 10) / 10;

  const runwayForScenarios =
    currentRunwayMonths === Infinity
      ? Number.POSITIVE_INFINITY
      : currentRunwayMonths;

  const fundingUrgency: FundingAnalysis["fundingUrgency"] =
    isInitialRaise
      ? netBurn > 0
        ? "high"
        : "low"
      : currentRunwayMonths === Infinity
        ? "low"
        : currentRunwayMonths <= 6
          ? "high"
          : currentRunwayMonths <= 12
            ? "medium"
            : "low";

  const optimalTiming = isInitialRaise
    ? "At launch (day zero)"
    : currentRunwayMonths === Infinity
      ? "Later"
      : currentRunwayMonths <= 6
        ? "Now"
        : currentRunwayMonths <= 12
          ? "Soon (3-6 months)"
          : "Plan ahead (6+ months)";

  return {
    perspective,
    monthlyBurn,
    monthlyRevenue,
    netBurn,
    currentRunwayMonths: isInitialRaise && netBurn > 0 ? 0 : currentRunwayMonths,
    fundingUrgency,
    optimalTiming,
    scenarios: buildScenarios(
      netBurn,
      cashUsedForScenarios,
      runwayForScenarios,
      isInitialRaise,
    ),
    analysisDate: effectiveDate,
    cashBalance: cash,
    cashUsedForScenarios,
    configuredInitialFunding,
  };
}
